import { useEffect, useState } from 'react';

type Mode = null | 'conflict' | 'taking-over' | 'superseded' | 'aborted';

/**
 * Welle 9: Single-Session für Berater. Existiert beim Login bereits eine aktive
 * Agenten-Sitzung, kann der Nutzer die andere beenden (übernehmen) oder abbrechen.
 * Companion- und Admin-Logins sind davon nicht betroffen (eigene/kein Socket).
 */
export function SessionConflictPrompt() {
  const [mode, setMode] = useState<Mode>(null);

  useEffect(() => {
    const onConflict = () => setMode((m) => (m === 'superseded' || m === 'aborted' ? m : 'conflict'));
    const onOk = () => setMode((m) => (m === 'conflict' || m === 'taking-over' ? null : m));
    const onSuperseded = () => setMode('superseded');
    window.addEventListener('bamoa:session-conflict', onConflict);
    window.addEventListener('bamoa:session-ok', onOk);
    window.addEventListener('bamoa:session-superseded', onSuperseded);
    return () => {
      window.removeEventListener('bamoa:session-conflict', onConflict);
      window.removeEventListener('bamoa:session-ok', onOk);
      window.removeEventListener('bamoa:session-superseded', onSuperseded);
    };
  }, []);

  if (!mode) return null;

  const takeover = () => { setMode('taking-over'); window.dispatchEvent(new Event('bamoa:do-session-takeover')); };
  const abort = () => { window.dispatchEvent(new Event('bamoa:do-session-abort')); setMode('aborted'); };

  return (
    <div style={overlay} role="dialog" aria-label="Sitzung">
      <div style={card}>
        {(mode === 'conflict' || mode === 'taking-over') && (
          <>
            <div style={titleStyle}>Bereits angemeldet</div>
            <div style={textStyle}>
              Dieser Account ist schon in einem anderen Fenster als Berater aktiv. Es kann immer
              nur eine Berater-Sitzung gleichzeitig laufen. Möchtest du die andere Sitzung beenden
              und hier weiterarbeiten?
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={takeover} disabled={mode === 'taking-over'} style={primaryBtn}>
                {mode === 'taking-over' ? 'Übernehme…' : 'Andere beenden & hier fortfahren'}
              </button>
              <button onClick={abort} disabled={mode === 'taking-over'} style={ghostBtn}>Abbrechen</button>
            </div>
          </>
        )}

        {mode === 'superseded' && (
          <>
            <div style={titleStyle}>Sitzung übernommen</div>
            <div style={textStyle}>
              Diese Berater-Sitzung wurde an anderer Stelle übernommen und ist hier nicht mehr aktiv.
              Du erhältst in diesem Fenster keine Anrufe mehr.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={() => window.location.reload()} style={primaryBtn}>Hier wieder übernehmen</button>
            </div>
          </>
        )}

        {mode === 'aborted' && (
          <>
            <div style={titleStyle}>Abgemeldet</div>
            <div style={textStyle}>
              Du arbeitest in der anderen Sitzung weiter. Dieses Fenster ist nicht aktiv und kann
              geschlossen werden.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={() => window.location.reload()} style={ghostBtn}>Doch hier übernehmen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
};
const card: React.CSSProperties = {
  width: 'min(460px, 92vw)', background: 'var(--bg-elev-1, #181818)', border: '1px solid var(--hairline, #2a2a2a)',
  borderRadius: 16, padding: '24px 26px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const titleStyle: React.CSSProperties = { fontSize: 18, fontWeight: 700, marginBottom: 8 };
const textStyle: React.CSSProperties = { fontSize: 13.5, lineHeight: 1.5, color: 'var(--fg-soft, #bbb)' };
const primaryBtn: React.CSSProperties = {
  flex: 1, padding: '11px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
  background: 'var(--tenant-accent, #E2001A)', color: '#fff', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit',
};
const ghostBtn: React.CSSProperties = {
  padding: '11px 18px', borderRadius: 10, border: '1px solid var(--hairline, #333)', cursor: 'pointer',
  background: 'transparent', color: 'var(--fg, #eee)', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
};
